import Button from "@components/buttons/Button"
import { Chevron } from "@components/icons"
import ImageHelper from "@components/ImageHelper"
import BulkOrderModal from "@components/Modal/BulkOrderModal"
import ProductSpecSheetsModal from "@components/Modal/ProductSpecSheetsModal"
import ProductSpecsModal from "@components/Modal/ProductSpecsModal"
import Prop65Modal from "@components/Modal/Prop65Modal"
import RequestReceivedModal from "@components/Modal/RequestReceivedModal"
import ShippingInfoModal from "@components/Modal/ShippingInfoModal"
import Stars from "@components/Stars"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import {
  body14Styles,
  paragraph18Styles,
  paragraph20Styles,
  paragraphStyles,
  smallcapsBold13Styles,
  subtextStyles,
} from "@constants/typography"
import { getIn } from "@utils/index"
import { DesktopOnly } from "@utils/MediaQuery"
import React, { useState } from "react"
import styled from "styled-components"

const Container = styled.div`
  @media ${breakpoints.laptop} {
    display: flex;
  }
`

const ContentWrapper = styled.div`
  flex-direction: column;
  display: flex;
  padding-top: 40px;

  @media ${breakpoints.tablet} {
    padding-top: 61px;
  }

  @media ${breakpoints.laptop} {
    width: calc(5 / 12 * 100vw);
    padding-top: 54px;
  }
`

const Description = styled.div`
  margin-bottom: 31px;
  padding: 0 20px;

  h6 {
    ${body14Styles}
    margin: 0 0 34px;
  }

  p {
    ${paragraph20Styles}
    margin: 0 0 34px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  p.finePrint {
    ${subtextStyles}
    margin: 0 0 12px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  ul {
    margin: 0 0 34px;
    padding-left: 18px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  li {
    color: ${colors.mediumDarkGray};

    p {
      ${paragraphStyles}
      color: ${colors.mediumDarkGray};
    }
  }

  @media ${breakpoints.tablet} {
    padding: 0 calc(2.5 / 12 * 100vw) 0 calc(1 / 12 * 100vw);
  }

  @media ${breakpoints.laptop} {
    margin-bottom: 22px;
    padding: 0;

    h6 {
      margin-bottom: 41px;
    }

    p {
      margin-bottom: 41px;
      font-size: 22px;
      line-height: 33px;
    }

    p.finePrint {
      ${subtextStyles}
      margin-bottom: 12px;
    }

    ul {
      margin-bottom: 41px;
    }

    li {
      p {
        ${paragraph18Styles}
        color: ${colors.mediumDarkGray};
      }
    }
  }
`

const Features = styled.div`
  display: flex;

  @media ${breakpoints.mobileOnly} {
    padding-left: 20px;
    flex-wrap: nowrap;
    overflow-x: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
    -webkit-overflow-scrolling: touch;

    ::-webkit-scrollbar {
      display: none;
    }
  }

  @media ${breakpoints.tablet} {
    width: 100%;
    padding: 0 calc(1 / 12 * 100vw);
    flex-wrap: wrap;
    align-content: flex-start;
  }

  @media ${breakpoints.laptop} {
    width: calc(4 / 12 * 100vw);
    margin-left: calc(1 / 12 * 100vw);
    flex-wrap: wrap;
    align-content: flex-start;
  }
`

const Feature = styled.a<{ hasLink?: boolean }>`
  display: flex;
  width: calc(0.8 / 2 * 100vw);
  min-height: calc(1.2 * 0.8 / 2 * 100vw);
  margin-right: 12px;
  padding: 10px 10px 12px;
  flex-grow: 0;
  flex-shrink: 0;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-end;
  background-color: ${colors.dividerLineGray};

  ${({ hasLink }) =>
    hasLink ? "pointer-events: initial;" : "pointer-events: none;"}

  @media ${breakpoints.tablet} {
    width: calc(2.25 / 12 * (100vw - 48px));
    height: auto;
    min-height: auto;
    margin: 0 16px 16px 0;
    padding: 16px;
    align-self: stretch;
    justify-content: flex-start;
  }

  @media ${breakpoints.laptop} {
    width: calc(3.5 / 3 / 12 * (100vw - 72px));
    height: auto;
    min-height: auto;
    margin: 0 24px 24px 0;
    padding: 20px;
    align-self: stretch;
    justify-content: flex-start;
  }
`

const FeatureIcon: any = styled.div`
  width: 80px;
  height: 80px;
  margin: 62px auto;

  @media ${breakpoints.tablet} {
    margin: 32px auto;
  }
`

const FeatureText = styled.div`
  ${body14Styles}
  padding-right: 10px;

  @media ${breakpoints.laptop} {
    color: ${colors.mediumDarkGray};
  }
`

const Buttons = styled.div`
  display: flex;
  flex-direction: row;
  margin: auto 0 0;
  flex-wrap: wrap;
`

const NormalButton = styled(Button)`
  display: flex;
  justify-content: space-between;
  > div {
    margin-left: 20px;
  }
  width: fit-content;
  margin: 0 24px 24px 0;
`

const InvertedButton = styled(Button)`
  display: flex;
  justify-content: space-between;
  > div {
    margin-left: 20px;
  }
  width: fit-content;
  margin: 0 24px 24px 0;
`

const MobileButtons = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 72px;

  @media ${breakpoints.tablet} {
    margin-top: 54px;
  }

  @media ${breakpoints.laptop} {
    display: none;
  }
`

const MobileButton = styled.button`
  ${smallcapsBold13Styles}
  display: flex;
  width: 100%;
  height: 56px;
  padding: 0 20px;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  color: ${colors.black};
  background: none;
  border: none;
  border-top: 1px solid ${colors.dividerLineGray};
  outline: none;

  @media ${breakpoints.tablet} {
    padding: 0 calc(1 / 12 * 100vw);
  }
`

const MobileButtonText = styled.span`
  padding-top: 4px;
`

const MobileButtonIcon = styled.div`
  height: 24px;
  width: 24px;
`

const RatingsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const Ratings = styled(Stars)`
  margin-right: 8px;
`

const DetailsBlock = ({
  avgRating,
  description,
  deviceAnchorText,
  features,
  numReviews,
  prop65Text,
  setShowReviews,
  showProp65Button,
  specs,
  specSheets,
}) => {
  const [showShippingInfo, setShippingInfo] = useState(false)
  const [showBulkOrder, setBulkOrder] = useState(false)
  const [showBulkOrderSubmitted, setBulkOrderSubmitted] = useState(false)
  const [showSpecs, setSpecs] = useState(false)
  const [showSpecSheets, setSpecSheets] = useState(false)
  const [showProp65, setProp65] = useState(false)

  return (
    <Container>
      <ContentWrapper>
        <Description>{description}</Description>
        {deviceAnchorText && (
          <Description id="deviceAnchorLink">{deviceAnchorText}</Description>
        )}
        <DesktopOnly>
          <Buttons>
            {specs.length > 0 && (
              <NormalButton onClick={() => setSpecs(true)} text="Full Specs" />
            )}
            {specSheets.length > 0 && (
              <NormalButton
                onClick={() => setSpecSheets(true)}
                text="Full Specs"
              />
            )}
            <InvertedButton
              onClick={() => setShippingInfo(true)}
              text="Shipping Info"
              white={true}
            />
            <InvertedButton
              onClick={() => setBulkOrder(true)}
              text="Bulk Ordering"
              white={true}
            />
            {showProp65Button && (
              <InvertedButton
                onClick={() => setProp65(true)}
                text="Prop 65"
                white={true}
              />
            )}
          </Buttons>
        </DesktopOnly>
      </ContentWrapper>
      <Features>
        {features.map((feature: any, idx: number) => {
          const linkProps: {
            hasLink?: boolean
            href?: string
            target?: string
            title?: string
          } = {}

          if (getIn(feature, "link")) {
            linkProps.hasLink = true
            linkProps.href = getIn(feature, "link")
            linkProps.title = getIn(feature, "description.description")
            linkProps.target = "_blank"
          }

          return (
            <Feature key={idx} {...linkProps}>
              <FeatureIcon>
                <ImageHelper src={getIn(feature, "icon.file.url")} />
              </FeatureIcon>
              <FeatureText>
                {getIn(feature, "description.description")}
              </FeatureText>
            </Feature>
          )
        })}
      </Features>
      <MobileButtons>
        {specs.length > 0 && (
          <MobileButton onClick={() => setSpecs(true)}>
            <MobileButtonText>Full Specs</MobileButtonText>
            <MobileButtonIcon>
              <Chevron />
            </MobileButtonIcon>
          </MobileButton>
        )}
        {specSheets.length > 0 && (
          <MobileButton onClick={() => setSpecSheets(true)}>
            <MobileButtonText>Full Specs</MobileButtonText>
            <MobileButtonIcon>
              <Chevron />
            </MobileButtonIcon>
          </MobileButton>
        )}
        <MobileButton onClick={() => setShowReviews(true)}>
          <MobileButtonText>{`Reviews (${numReviews})`}</MobileButtonText>
          <RatingsContainer>
            <Ratings stars={Math.round(avgRating)} />
            <MobileButtonIcon>
              <Chevron />
            </MobileButtonIcon>
          </RatingsContainer>
        </MobileButton>
        <MobileButton onClick={() => setShippingInfo(true)}>
          <MobileButtonText>Shipping Info</MobileButtonText>
          <MobileButtonIcon>
            <Chevron />
          </MobileButtonIcon>
        </MobileButton>
        <MobileButton onClick={() => setBulkOrder(true)}>
          <MobileButtonText>Bulk Ordering</MobileButtonText>
          <MobileButtonIcon>
            <Chevron />
          </MobileButtonIcon>
        </MobileButton>
        {showProp65Button && (
          <MobileButton onClick={() => setProp65(true)}>
            <MobileButtonText>Prop 65</MobileButtonText>
            <MobileButtonIcon>
              <Chevron />
            </MobileButtonIcon>
          </MobileButton>
        )}
      </MobileButtons>
      <ShippingInfoModal
        isOpen={showShippingInfo}
        onRequestClose={() => setShippingInfo(false)}
      />
      <BulkOrderModal
        isOpen={showBulkOrder}
        onRequestClose={() => setBulkOrder(false)}
        showSuccess={() => {
          setBulkOrderSubmitted(true)
          setBulkOrder(false)
        }}
      />
      <RequestReceivedModal
        isOpen={showBulkOrderSubmitted}
        onRequestClose={() => setBulkOrderSubmitted(false)}
      />
      <ProductSpecsModal
        isOpen={showSpecs}
        onRequestClose={() => setSpecs(false)}
        specs={specs}
      />
      <ProductSpecSheetsModal
        isOpen={showSpecSheets}
        onRequestClose={() => setSpecSheets(false)}
        specSheets={specSheets}
      />
      <Prop65Modal
        content={prop65Text}
        isOpen={showProp65}
        onRequestClose={() => setProp65(false)}
      />
    </Container>
  )
}

export default DetailsBlock
